import { uniqWith } from 'es-toolkit';

import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import {
  BevelCut,
  BottomBevelCut,
  BraceInstallationLocation,
  Component,
  ComponentDesign,
  Lumber,
  Member,
  MemberLine,
  MemberType,
  Point2D,
  RelativeMemberGeometry,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { lumbersAreEqual, lumbersAreEqualExcludingSize } from '@paragon/material-lists';
import { enqueueError } from '@paragon/notification-tools';
import { cutMember, CutMemberOptions, getAllMembers } from '@paragon/relative-geometry';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { calculateMemberPolygon } from '@paragon/three-shared';

import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { setMembers } from '../slices/componentDesignsSlice';
import { setUnsavedComponentDesignFromServer, updateFeatures } from './appActions';
import { getMemberLine, getNewMemberName } from './insertActions';
import { loadAdditionalLumberInformation } from './loadActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const setMemberTypes =
  (componentDesignGuid: string, members: readonly Member[], newType: MemberType): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesign = getState().componentDesigns.data[componentDesignGuid];
    const { component } = componentDesign;
    const memberGuids = new Set(members.map((m) => m.guid));
    const usedNames = getAllMembers(component)
      .filter((m) => !memberGuids.has(m.guid))
      .map((m) => m.name);
    const newMembers = members.map((member) => {
      const name = getNewMemberName(newType, usedNames);
      usedNames.push(name);
      return { ...member, memberType: newType, name };
    });
    dispatch(setMembers({ componentDesignGuid, members: newMembers }));
  };

/**
 * Set the insertion member or member on the component, generating cuts as needed.
 */
const setMemberWithGeneratedCuts =
  (componentDesign: ComponentDesign, newMember: Member, options: CutMemberOptions): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { component } = componentDesign;

    let member = newMember;
    if (member.relativeGeometry && !calculateMemberPolygon(newMember, component)) {
      // The component used for cutting should not have the member being cut as part of it.
      const componentWithoutMember: Component = {
        ...component,
        members: component.members.filter((member) => member.guid !== newMember.guid),
      };
      const cutRelativeGeometry = generateCutsForMember(member.relativeGeometry, componentWithoutMember, options);
      if (cutRelativeGeometry) {
        member = { ...member, relativeGeometry: cutRelativeGeometry };
      }
    }

    dispatch(setMembers({ componentDesignGuid: componentDesign.guid, members: [member] }));

    await dispatch(updateFeatures());
  };

function generateCutsForMember(
  memberRelativeGeometry: RelativeMemberGeometry,
  componentWithoutMember: Component,
  options: CutMemberOptions,
): RelativeMemberGeometry | null {
  try {
    const { baseCutGuids, endCutGuids, lineLocation } = cutMember(
      memberRelativeGeometry,
      componentWithoutMember,
      options,
    );
    if (baseCutGuids.length === 0 || endCutGuids.length === 0) return null;
    return { ...memberRelativeGeometry, endCutGuids, baseCutGuids, lineLocation };
  } catch {
    // continue regardless of error
    return null;
  }
}

export const setMemberLine =
  (componentDesign: ComponentDesign, member: Member, lineGuid: string, locationHint?: Point2D): AsyncThunkActionVoid =>
  async (dispatch) => {
    if (!member || !member.relativeGeometry) {
      throw new Error('Attempted to add line to invalid member.');
    }

    const newMember: Member = {
      ...member,
      relativeGeometry: { ...member.relativeGeometry, lineGuid, baseCutGuids: [], endCutGuids: [] },
    };
    await dispatch(
      setMemberWithGeneratedCuts(componentDesign, newMember, {
        locationHint: locationHint ?? null,
        guessLineLocation: true,
      }),
    );
  };

export const setMemberLineLocation =
  (componentDesignGuid: string, member: Member, lineLocation: MemberLine['lineLocation']): AsyncThunkActionVoid =>
  async (dispatch) => {
    if (!member.relativeGeometry) throw new Error('Member has no relative geometry.');
    if (member.relativeGeometry.lineLocation === lineLocation) return;

    const newMember: Member = { ...member, relativeGeometry: { ...member.relativeGeometry, lineLocation } };

    dispatch(setMembers({ componentDesignGuid, members: [newMember] }));
    await dispatch(updateFeatures());
  };

export const setBraceSide =
  (componentDesignGuid: string, member: Member, side: MemberLine['lineLocation']): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (!member.relativeGeometry || !member.relativeGeometry.isBrace) {
      throw new Error();
    }

    const { component } = getState().componentDesigns.data[componentDesignGuid];
    const { bracedMemberGuid } = member.relativeGeometry;
    const bracedMember = component.members.find((brace) => brace.guid === bracedMemberGuid)!;
    // As in `finishSetMemberForInsertionBrace`, when on the face, use the line location of the braced member. See the
    // note there.
    const sideForLineGuid = side === 'Center' ? bracedMember.relativeGeometry!.lineLocation : side;
    const lineGuid = getMemberLine(
      component.referenceLines!,
      bracedMember.relativeGeometry!.guid,
      sideForLineGuid,
    )!.guid;
    const memberType = side === 'Center' ? 'Scab' : 'ExtraplanarBrace';
    const lineLocation = side === 'Port' ? 'Starboard' : side === 'Starboard' ? 'Port' : sideForLineGuid;
    const newMember: Member = {
      ...member,
      relativeGeometry: { ...member.relativeGeometry, lineLocation, side, lineGuid },
      memberType,
      name: member.name,
    };
    dispatch(setMembers({ componentDesignGuid, members: [newMember] }));
    await dispatch(updateFeatures());

    // editing the lumber without any new properties to update gives a brace a chance to update its width & thickness
    dispatch(editLumbers(componentDesignGuid, [[newMember, newMember.lumber]]));
  };

export const setBraceInstallationLocation =
  (
    componentDesignGuid: string,
    member: Member,
    braceInstallationLocation: BraceInstallationLocation,
  ): ThunkActionVoid =>
  (dispatch) => {
    if (!member.relativeGeometry?.isBrace) {
      throw new Error();
    }

    if (member.relativeGeometry.braceInstallationLocation === braceInstallationLocation) {
      return;
    }
    const newMember = { ...member, relativeGeometry: { ...member.relativeGeometry, braceInstallationLocation } };
    dispatch(setMembers({ componentDesignGuid, members: [newMember] }));
  };

export const setMemberBevelCut =
  (
    componentDesignGuid: string,
    member: Member,
    memberSide: 'Left' | 'Right',
    newBevelCut: BevelCut | null,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    const bevelCutPropertyName = memberSide === 'Left' ? ('leftBevelCut' as const) : ('rightBevelCut' as const);
    const newMember = {
      ...member,
      [bevelCutPropertyName]: newBevelCut,
    };
    dispatch(setMembers({ componentDesignGuid, members: [newMember] }));
    await dispatch(updateFeatures());
  };

export const setMemberBottomBevelCut =
  (componentDesignGuid: string, members: readonly Member[], newBevelCut: BottomBevelCut | null): AsyncThunkActionVoid =>
  async (dispatch) => {
    const newMembers = members.map((member) => ({ ...member, bottomBevelCut: newBevelCut }));
    dispatch(setMembers({ componentDesignGuid, members: newMembers }));
    await dispatch(updateFeatures());
  };

export const editLumbers = (componentDesignGuid: string, updates: [Member, Lumber][]) => {
  const lockMembers = updates.some(([member, lumber]) => !lumbersAreEqualExcludingSize(member.lumber, lumber));
  return setMembersWithNewLumber(componentDesignGuid, updates, { lockMembers });
};

const setMembersWithNewLumber =
  (
    componentDesignGuid: string,
    updatedMembers: [Member, Lumber][],
    options?: { lockMembers?: boolean },
  ): ThunkActionVoid =>
  (dispatch) => {
    const uniqueLumbers = uniqWith(
      updatedMembers.map(([, lumber]) => lumber),
      lumbersAreEqual,
    );

    for (const lumber of uniqueLumbers) {
      dispatch(loadAdditionalLumberInformation(lumber));
    }

    const newMembers = updatedMembers.map(([member, lumber]) => {
      const width = member.relativeGeometry?.isBrace
        ? member.relativeGeometry.side === 'Center'
          ? Math.max(lumber.actualThickness, lumber.actualWidth)
          : Math.min(lumber.actualThickness, lumber.actualWidth)
        : lumber.actualThickness === member.thickness
          ? lumber.actualWidth
          : lumber.actualThickness;

      const thickness = width === lumber.actualWidth ? lumber.actualThickness : lumber.actualWidth;
      const relativeGeometry = member.relativeGeometry ? { ...member.relativeGeometry, width } : null;

      return { ...member, thickness, lumber, relativeGeometry };
    });

    if (newMembers.some((member) => member.relativeGeometry == null)) {
      dispatch(enqueueError('Member does not have relative geometry! Cannot update geometry from lumber.'));
    }

    dispatch(setMembers({ componentDesignGuid, members: newMembers, lockMembers: options?.lockMembers }));
    dispatch(updateFeatures());
  };

export const setMemberIsFieldInstalled =
  (componentDesignGuid: string, member: Member, isFieldInstalled: boolean): ThunkActionVoid =>
  (dispatch) => {
    const newMember: Member = {
      ...member,
      isFieldInstalled,
    };

    dispatch(setMembers({ componentDesignGuid, members: [newMember] }));
  };

export const stackChord =
  (
    componentDesign: ComponentDesign,
    member: Member,
    startLocation: number,
    endLocation: number,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    if (componentDesign) {
      const spinnerId = dispatch(addSpinnerMessage('Stacking chord...'));

      try {
        const newComponentDesign = await designService.componentDesigns.postStackChord(
          componentDesign,
          member.guid,
          startLocation,
          endLocation,
        );
        dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
      } catch (exception) {
        console.error(exception);
        dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error stacking chord.')));
      } finally {
        dispatch(removeSpinnerMessage(spinnerId));
      }
    }
  };

export const unstackChord = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const componentDesign = getActiveComponentDesign(state);
  const selectedEntry = getSingleSelectedItemEntryOrNull(state);

  if (!componentDesign) return;

  if (selectedEntry?.type !== 'member') {
    throw new Error('A member must be selected.');
  }

  const spinnerId = dispatch(addSpinnerMessage('Unstacking chord...'));
  try {
    const newComponentDesign = await designService.componentDesigns.postUnstackChord(
      componentDesign,
      selectedEntry.item.guid,
    );
    dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
  } catch (exception) {
    console.error(exception);
    dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error unstacking chord.')));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

const webEdit =
  (
    spinnerMessage: string,
    webAction: (componentDesign: ComponentDesign) => Promise<ComponentDesign>,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) return;

    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    try {
      const newComponentDesign = await webAction(componentDesign);
      dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
    } catch (exception) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error while editing web.')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const stackWeb =
  (memberGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(
      webEdit('Stacking Web...', (componentDesign) =>
        designService.componentDesigns.postStackWeb(componentDesign, memberGuid),
      ),
    );
  };

export const unstackWeb =
  (memberGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(
      webEdit('Removing Web...', (componentDesign) =>
        designService.componentDesigns.postUnstackWeb(componentDesign, memberGuid),
      ),
    );
  };

export const reverseWeb =
  (memberGuid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(
      webEdit('Reversing Web...', (componentDesign) =>
        designService.componentDesigns.postReverseWeb(componentDesign, memberGuid),
      ),
    );
  };

export const makeWebVertical =
  (memberGuid: string, adjustLowerEnd: boolean): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(
      webEdit('Making Web Vertical...', (componentDesign: ComponentDesign) =>
        designService.componentDesigns.postMakeWebVertical(componentDesign, memberGuid, adjustLowerEnd),
      ),
    );
  };

export const adjustCut =
  (memberGuid: string, adjustLowerEnd: boolean, shiftClockwise: boolean): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(
      webEdit('Recutting...', (componentDesign: ComponentDesign) =>
        designService.componentDesigns.postAdjustCut(componentDesign, memberGuid, adjustLowerEnd, shiftClockwise),
      ),
    );
  };
